import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { mainLogin, clearError } from '../../features/auth/authSlice';
import { profilePathFor } from '../../utils/profileLinks';

const inputClass =
  'w-full px-5 py-4 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition text-base';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    // Clear error when component mounts
    dispatch(clearError());

    // Clear error when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const resultAction = await dispatch(mainLogin(formData));

    if (mainLogin.fulfilled.match(resultAction)) {
      // Get the user from the result action payload (not from state, as state might not be updated yet)
      const loggedInUser = resultAction.payload.user;
      console.log('user after login:', loggedInUser);

      // Visitors who clicked a player or team on the homepage go straight to that profile
      const profilePath = profilePathFor(loggedInUser?.role, location.state?.redirectTo);
      if (profilePath) {
        navigate(profilePath);
        return;
      }

      // Navigate based on user role
      if (loggedInUser?.role === 'player') {
        navigate('/player/dashboard');
      } else if (loggedInUser?.role === 'coach') {
        navigate('/coach/dashboard');

      } else if (loggedInUser?.role === 'scout') {
        navigate('/scout/dashboard');

      } else if (loggedInUser?.role === 'admin' || loggedInUser?.role === 'superAdmin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  };

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-md mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            Welcome Back
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            SIGN IN
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            Use the email and password you signed up with.
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          <div className="rounded-xl p-6 sm:p-10 bg-jp-section border border-jp-outline/50">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="login-email" className="block text-base font-semibold text-jp-ink mb-2">Email</label>
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  className={inputClass}
                />
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <label htmlFor="login-password" className="block text-base font-semibold text-jp-ink">Password</label>
                  <button
                    type="button"
                    onClick={() => navigate('/auth/forgot-password')}
                    className="text-sm font-semibold text-jp-accent hover:underline focus:outline-none focus-visible:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  id="login-password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                  className={inputClass}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>

          <div className="px-2 pt-5 pb-1 space-y-2 text-center text-sm sm:text-base text-jp-muted">
            <p>
              Don't have an account?{' '}
              <Link to="/auth/signup" className="font-semibold text-jp-accent hover:underline">
                Sign up
              </Link>
            </p>
            <p>
              Are you a player?{' '}
              <Link to="/auth/login-player" className="font-semibold text-jp-accent hover:underline">
                Find your player profile
              </Link>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
